import { IconChevronDown, IconMenu2 } from "@tabler/icons-react"
import { type FC } from "react"

import {
	getLandingPageNavigationItems,
	hasLandingPageNavigation,
	headerNavigationDropdownThreshold,
	type LandingPageHeaderBlock,
	type LandingPageNavigationItem,
	type LandingPageNavigationPage,
} from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import { usePlatform } from "~/contexts/UserContext"

type Props = {
	block: LandingPageHeaderBlock
	navigationPages: LandingPageNavigationPage[]
	currentPath: string
}
const HeaderBlockPreview: FC<Props> = ({ block, navigationPages, currentPath }) => {
	const platform = usePlatform()!
	const navigationItems = getLandingPageNavigationItems(navigationPages)
	const showNav = hasLandingPageNavigation(navigationPages)
	const isAuthVisible = block.content.isAuthVisible
	const hasNavigationGroups = navigationItems.some(item => item.type === "group")
	const useDropdown = !hasNavigationGroups && navigationPages.length > headerNavigationDropdownThreshold

	return (
		<header className="border-landing-header-foreground/10 bg-landing-header/80 supports-backdrop-filter:bg-landing-header/60 border-b backdrop-blur-xl">
			<nav className="mx-auto box-content flex max-w-7xl items-center justify-between gap-4 px-6 py-4 lg:px-8">
				{platform.logo ? (
					platform.logo.isIcon ? (
						<div className="flex items-center gap-3">
							<img
								src={platform.logo.filePath}
								alt="Logo"
								className="h-10 w-auto max-w-20 shrink-0 object-contain sm:h-12 sm:max-w-24"
							/>
							<span className="text-landing-header-foreground text-lg font-semibold tracking-tight">
								{platform.name}
							</span>
						</div>
					) : (
						<div className="min-w-0 shrink">
							<img
								src={platform.logo.filePath}
								alt="Logo"
								className="h-10 w-auto max-w-56 object-contain object-left sm:h-12 sm:max-w-80"
							/>
						</div>
					)
				) : (
					<span className="text-landing-header-foreground text-lg font-semibold tracking-tight">
						{platform.name}
					</span>
				)}

				{showNav && !useDropdown && (
					<div className="hidden items-center gap-6 lg:flex">
						{navigationItems.map(item =>
							item.type === "page" ? (
								<PreviewNavigationPage key={item.page.id} page={item.page} currentPath={currentPath} />
							) : (
								<PreviewNavigationGroup key={item.label} item={item} currentPath={currentPath} />
							),
						)}
					</div>
				)}

				{showNav && useDropdown && (
					<div className="border-landing-header-foreground/15 bg-landing-header-foreground/5 text-landing-header-foreground/80 hidden items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm lg:flex">
						<span>Menu</span>
						<IconChevronDown className="size-4" />
					</div>
				)}

				<div className="flex items-center gap-4">
					{isAuthVisible && (
						<div className="hidden items-center gap-8 lg:flex">
							<span className="text-landing-header-foreground/70 text-sm font-medium">Sign in</span>
							<span className="landing-button px-5 py-2.5 text-sm font-semibold">Sign up</span>
						</div>
					)}
					{(showNav || isAuthVisible) && (
						<div className="text-landing-header-foreground/70 rounded-md p-2 lg:hidden">
							<IconMenu2 size={22} />
						</div>
					)}
				</div>
			</nav>
		</header>
	)
}

type PreviewNavigationPageProps = {
	page: LandingPageNavigationPage
	currentPath: string
}
const PreviewNavigationPage: FC<PreviewNavigationPageProps> = ({ page, currentPath }) => {
	const isActive = !page.isExternal && page.path === currentPath
	return (
		<span
			className={cn(
				isActive
					? "text-landing-header-foreground text-sm font-semibold"
					: "text-landing-header-foreground/70 text-sm font-medium",
			)}
		>
			{page.title ?? page.path}
		</span>
	)
}

type PreviewNavigationGroupProps = {
	item: Extract<LandingPageNavigationItem, { type: "group" }>
	currentPath: string
}
const PreviewNavigationGroup: FC<PreviewNavigationGroupProps> = ({ item, currentPath }) => {
	const isActive = item.pages.some(page => !page.isExternal && page.path === currentPath)
	return (
		<div
			className={cn(
				"flex items-center gap-1.5 rounded-full px-2 py-1 text-sm font-medium",
				isActive ? "text-landing-header-foreground font-semibold" : "text-landing-header-foreground/70",
			)}
		>
			<span>{item.label}</span>
			<IconChevronDown className="size-4" />
		</div>
	)
}

export default HeaderBlockPreview
